import axios from "axios";

import {
    CreateVideoCommentRequestDTO,
    DeleteVideoCommentRequestDTO,
    EditVideoCommentRequestDTO,
    PinnedVideoCommentDTO,
    ResolveVideoCommentReportRequestDTO,
    SubmittedVideoCommentReportDTO,
    SubmitVideoCommentReportRequestDTO,
    VideoCommentDTO,
    VideoCommentErrorDTO,
    VideoCommentModerationPageDTO,
    VideoCommentReportResolutionDTO,
    VideoCommentReportStatus,
    VideoCommentRepliesPageDTO,
    VideoCommentsPageDTO,
    VideoCommentSort,
    VideoCommentVoteDTO,
} from "../Models/DTOs/VideoCommentsDTO";

const WEB_API_BASE = (process.env.REACT_APP_WEB_API_URL ?? "").replace(/\/$/, "");

const commentsUrl = (path: string): string => {
    if (!WEB_API_BASE) {
        throw new Error("REACT_APP_WEB_API_URL is not set.");
    }
    return `${WEB_API_BASE}/v1${path}`;
};

const authorizationHeaders = (accessToken: string | null) =>
    accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;

export const getVideoCommentErrorCode = (error: unknown): string | null => {
    if (!axios.isAxiosError<VideoCommentErrorDTO>(error)) return null;
    return error.response?.data?.error ?? null;
};

export const getVideoCommentErrorStatus = (error: unknown): number | null => {
    if (!axios.isAxiosError(error)) return null;
    return error.response?.status ?? null;
};

export const isVideoCommentRequestCancelled = (error: unknown): boolean =>
    axios.isCancel(error) || error instanceof axios.CanceledError;

// Article and video comment IDs are isolated; choose the route family once per client.
export type CommentContentType = "video" | "article";

export function createCommentsApi(contentType: CommentContentType) {
    const contentPath = contentType === "article" ? "articles" : "videos";
    const commentPath = contentType === "article" ? "article-comments" : "comments";

    async function getComments(
        contentId: number,
        sort: VideoCommentSort,
        take: number,
        cursor: string | null,
        accessToken: string | null,
        abortController: AbortController
    ): Promise<VideoCommentsPageDTO> {
        const response = await axios.get<VideoCommentsPageDTO>(
            commentsUrl(`/${contentPath}/${contentId}/comments`),
            {
                params: { sort, take, ...(cursor ? { cursor } : {}) },
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function getReplies(
        commentId: number,
        take: number,
        cursor: string | null,
        accessToken: string | null,
        abortController: AbortController
    ): Promise<VideoCommentRepliesPageDTO> {
        const response = await axios.get<VideoCommentRepliesPageDTO>(
            commentsUrl(`/${commentPath}/${commentId}/replies`),
            {
                params: { take, ...(cursor ? { cursor } : {}) },
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function createComment(
        contentId: number,
        request: CreateVideoCommentRequestDTO,
        idempotencyKey: string,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentDTO> {
        const response = await axios.post<VideoCommentDTO>(
            commentsUrl(`/${contentPath}/${contentId}/comments`),
            request,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Idempotency-Key": idempotencyKey,
                },
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function editComment(
        commentId: number,
        request: EditVideoCommentRequestDTO,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentDTO> {
        const response = await axios.patch<VideoCommentDTO>(
            commentsUrl(`/${commentPath}/${commentId}`),
            request,
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function deleteComment(
        commentId: number,
        request: DeleteVideoCommentRequestDTO,
        accessToken: string,
        abortController: AbortController
    ): Promise<void> {
        await axios.delete(commentsUrl(`/${commentPath}/${commentId}`), {
            data: request,
            headers: authorizationHeaders(accessToken),
            signal: abortController.signal,
        });
    }

    async function setVote(
        commentId: number,
        vote: 1 | -1,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentVoteDTO> {
        const response = await axios.put<VideoCommentVoteDTO>(
            commentsUrl(`/${commentPath}/${commentId}/vote`),
            { vote },
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function removeVote(
        commentId: number,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentVoteDTO> {
        const response = await axios.delete<VideoCommentVoteDTO>(
            commentsUrl(`/${commentPath}/${commentId}/vote`),
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function reportComment(
        commentId: number,
        request: SubmitVideoCommentReportRequestDTO,
        accessToken: string,
        abortController: AbortController
    ): Promise<SubmittedVideoCommentReportDTO> {
        const response = await axios.post<SubmittedVideoCommentReportDTO>(
            commentsUrl(`/${commentPath}/${commentId}/reports`),
            request,
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function getModerationReports(
        status: VideoCommentReportStatus,
        take: number,
        cursor: string | null,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentModerationPageDTO> {
        const response = await axios.get<VideoCommentModerationPageDTO>(
            commentsUrl(`/admin/${commentPath}/reports`),
            {
                params: { status, take, ...(cursor ? { cursor } : {}) },
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function resolveReport(
        reportId: number,
        request: ResolveVideoCommentReportRequestDTO,
        accessToken: string,
        abortController: AbortController
    ): Promise<VideoCommentReportResolutionDTO> {
        const response = await axios.patch<VideoCommentReportResolutionDTO>(
            commentsUrl(`/admin/${commentPath}/reports/${reportId}`),
            request,
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function pinComment(
        contentId: number,
        commentId: number,
        accessToken: string,
        abortController: AbortController
    ): Promise<PinnedVideoCommentDTO> {
        const response = await axios.put<PinnedVideoCommentDTO>(
            commentsUrl(`/${contentPath}/${contentId}/pinned-comment/${commentId}`),
            undefined,
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
        return response.data;
    }

    async function unpinComment(
        contentId: number,
        commentId: number,
        accessToken: string,
        abortController: AbortController
    ): Promise<void> {
        await axios.delete(
            commentsUrl(`/${contentPath}/${contentId}/pinned-comment/${commentId}`),
            {
                headers: authorizationHeaders(accessToken),
                signal: abortController.signal,
            }
        );
    }

    return {
        getComments, getReplies, createComment, editComment, deleteComment,
        setVote, removeVote, reportComment, getModerationReports, resolveReport,
        pinComment, unpinComment,
    };
}

export type CommentsApi = ReturnType<typeof createCommentsApi>;